// API service for user operations
import { fetchMockUsers, searchMockUsersByEmail } from './mockUserService';
import { database } from './firebase';
import { ref, get } from 'firebase/database';

// Firebase Functions API URL
// The function is deployed as 'api' and Express routes already include '/api'
// So the full path is: /api (function name) + /api (Express route) + /users (endpoint)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://us-central1-gobrummi.cloudfunctions.net/api/api';

// Toggle this to switch between mock data and real API
const USE_MOCK_DATA = false;

// Client-side cache for better performance
let userCache: AuthUser[] = [];
let lastCacheTime = 0;
const CACHE_EXPIRY = 2 * 60 * 1000; // 2 minutes in milliseconds

export interface AuthUser {
  uid: string;
  email: string | null;
  lastSignInTime?: string;
  creationTime: string;
  disabled: boolean;
  emailVerified: boolean;
  displayName?: string;
  phoneNumber?: string;
  photoURL?: string;
  // Custom fields for display
  status?: string;
  formattedLastLogin?: string;
  formattedCreatedAt?: string;
}

export interface UserSettings {
  [key: string]: any;
}

export interface Vehicle {
  id: string;
  name?: string;
  type?: string;
  [key: string]: any;
}

/**
 * Fetch all users (paginated)
 */
export const fetchUsers = async (pageToken?: string): Promise<{users: AuthUser[], pageToken?: string}> => {
  try {
    if (USE_MOCK_DATA) {
      return await fetchMockUsers(pageToken);
    }
    
    const url = pageToken 
      ? `${API_BASE_URL}/users?pageToken=${pageToken}`
      : `${API_BASE_URL}/users`;
      
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Update cache when fetching all users
    if (!pageToken) {
      userCache = data.users;
      lastCacheTime = Date.now();
    } else if (data.users.length > 0) {
      // Append to cache for subsequent pages
      userCache = [...userCache, ...data.users];
      lastCacheTime = Date.now();
    }
    
    return data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Search users by email (DEPRECATED - searches Firebase Auth which is slow)
 */
export const searchUsersByEmail = async (email: string): Promise<AuthUser[]> => {
  try {
    if (USE_MOCK_DATA) {
      return await searchMockUsersByEmail(email);
    }

    console.log('Searching users with email:', email);

    // Always fetch from server for searching by email to ensure accurate results
    const response = await fetch(`${API_BASE_URL}/users/search?email=${encodeURIComponent(email)}`);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Search results received:', data.users.length);
    return data.users;
  } catch (error) {
    console.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Search for a user ID in the userDB by email address via backend API
 * Returns the user ID (document ID) if found
 */
export const searchUserIdByEmail = async (email: string): Promise<string | null> => {
  try {
    if (USE_MOCK_DATA) {
      // Mock implementation
      const users = await searchMockUsersByEmail(email);
      return users.length > 0 ? users[0].uid : null;
    }

    console.log('Searching userDB for email via API:', email);

    // Call the backend API endpoint to search userDB
    const response = await fetch(`${API_BASE_URL}/users/searchByEmail?email=${encodeURIComponent(email)}`);

    if (response.ok) {
      const data = await response.json();
      console.log('Found user ID:', data.userId);
      return data.userId;
    } else if (response.status === 404) {
      console.log('No user found with email:', email);
      return null;
    } else {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error searching userDB by email:', error);
    throw error;
  }
};

/**
 * Get a user by their UID
 */
export const getUserById = async (uid: string): Promise<AuthUser> => {
  try {
    if (USE_MOCK_DATA) {
      // Find user in mock data
      const users = await searchMockUsersByEmail('');
      const user = users.find(u => u.uid === uid);
      if (!user) throw new Error(`User with UID ${uid} not found`);
      return user;
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${uid}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.user;
  } catch (error) {
    console.error(`Error fetching user ${uid}:`, error);
    throw error;
  }
};

/**
 * Update a user
 */
export const updateUser = async (uid: string, updates: Partial<AuthUser>): Promise<AuthUser> => {
  try {
    if (USE_MOCK_DATA) {
      // Simulated update - in reality, we would persist this
      const users = await searchMockUsersByEmail('');
      const userIndex = users.findIndex(u => u.uid === uid);
      if (userIndex === -1) throw new Error(`User with UID ${uid} not found`);
      
      // Update the user object with the new values
      const updatedUser = {
        ...users[userIndex],
        ...updates,
        // Ensure these fields don't get overwritten if not provided in updates
        uid: users[userIndex].uid
      };
      
      return updatedUser;
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${uid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.user;
  } catch (error) {
    console.error(`Error updating user ${uid}:`, error);
    throw error;
  }
};

/**
 * Delete a user
 */
export const deleteUser = async (uid: string): Promise<void> => {
  try {
    if (USE_MOCK_DATA) {
      // In a real implementation, we would persist this deletion
      console.log(`Mock deletion of user with UID ${uid}`);
      return;
    }
    
    const response = await fetch(`${API_BASE_URL}/users/${uid}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error(`Error deleting user ${uid}:`, error);
    throw error;
  }
};

/**
 * Get user settings from Firebase Realtime Database
 */
export const getUserSettings = async (uid: string): Promise<UserSettings | null> => {
  try {
    if (USE_MOCK_DATA) {
      // Mock data for testing
      return {
        premium: false,
        language: 'en',
        notifications: true,
        theme: 'light',
        lastUpdated: new Date().toISOString()
      };
    }

    // Reference to the user settings in Firebase Realtime Database
    const userDbRef = ref(database, `/userdb/${uid}`);

    // Get the data once
    const snapshot = await get(userDbRef);

    if (snapshot.exists()) {
      return snapshot.val();
    } else {
      console.log(`No settings found for user ${uid}`);
      return null;
    }
  } catch (error) {
    console.error(`Error fetching user settings for ${uid}:`, error);
    throw error;
  }
};

/**
 * Get user vehicles from Firebase Realtime Database (truckDB)
 */
export const getUserVehicles = async (uid: string): Promise<Vehicle[]> => {
  try {
    if (USE_MOCK_DATA) {
      // Mock data for testing
      return [
        {
          id: 'vehicle1',
          name: 'Test Truck 1',
          type: 'Semi-Trailer',
          licensePlate: 'ABC-123',
          capacity: 20000
        },
        {
          id: 'vehicle2',
          name: 'Test Van',
          type: 'Cargo Van',
          licensePlate: 'XYZ-789',
          capacity: 5000
        }
      ];
    }

    // Reference to the user's trucks in Firebase Realtime Database
    const truckDbRef = ref(database, `/truckDB/${uid}`);

    // Get the data once
    const snapshot = await get(truckDbRef);

    if (snapshot.exists()) {
      const vehiclesData = snapshot.val();
      // Convert object to array with IDs
      const vehicles: Vehicle[] = Object.entries(vehiclesData).map(([vehicleId, data]: [string, any]) => ({
        ...data,
        id: vehicleId  // Ensure the document ID is set as the id field
      }));
      return vehicles;
    } else {
      console.log(`No vehicles found for user ${uid}`);
      return [];
    }
  } catch (error) {
    console.error(`Error fetching user vehicles for ${uid}:`, error);
    throw error;
  }
};
